import * as Sentry from '@sentry/react'

// Initialize Sentry for error tracking
export function initializeMonitoring() {
  if (import.meta.env.PROD && import.meta.env.VITE_SENTRY_DSN) {
    Sentry.init({
      dsn: import.meta.env.VITE_SENTRY_DSN,
      environment: import.meta.env.MODE,
      tracesSampleRate: 0.1,
      beforeSend(event) {
        // Filter out non-critical errors
        if (event.exception) {
          const error = event.exception.values?.[0]
          if (error?.type === 'ChunkLoadError' || error?.type === 'NetworkError') {
            return null
          }
        }
        return event
      }
    })
  }
}

// Analytics tracking
interface AnalyticsEvent {
  event: string
  properties?: Record<string, any>
  userId?: string
}

export function trackEvent({ event, properties, userId }: AnalyticsEvent) {
  try {
    // Log to console in development
    if (import.meta.env.DEV) {
      console.log('📊 Analytics Event:', { event, properties, userId })
    }

    // In production, send to analytics service
    if (import.meta.env.PROD) {
      // Placeholder for analytics service integration
      // Example: analytics.track(event, properties, userId)
    }
  } catch (error) {
    console.error('Analytics tracking error:', error)
  }
}

// Error logging with context
export function logError(error: Error, context?: Record<string, any>) {
  console.error('Application Error:', error, context)
  
  if (import.meta.env.PROD) {
    Sentry.captureException(error, {
      extra: context,
      tags: {
        component: context?.component || 'unknown',
        action: context?.action || 'unknown'
      }
    })
  } else {
    // In development, provide more detailed error information
    console.group('🚨 Error Details')
    console.error('Error:', error.message)
    console.error('Stack:', error.stack)
    console.error('Context:', context)
    console.groupEnd()
  }
}

// Performance monitoring
export function measurePerformance(name: string, fn: () => Promise<any>) {
  return async (...args: any[]) => {
    const start = performance.now()
    try {
      const result = await fn(...args)
      const duration = performance.now() - start
      
      if (import.meta.env.DEV) {
        console.log(`⚡ Performance: ${name} took ${duration.toFixed(2)}ms`)
      }
      
      // Track slow operations
      if (duration > 1000) {
        trackEvent({
          event: 'slow_operation',
          properties: { operation: name, duration }
        })
      }
      
      return result
    } catch (error) {
      logError(error as Error, { operation: name })
      throw error
    }
  }
}